import { useEffect, useRef, useState } from "react";
import type { ActionResult, DeviceType } from "@/types";
import type {
  LiveRecordingStatus,
  RecordingHealth,
  RecordingHealthStatus,
  RecordingRecoveryStatus,
  StopRecordingError,
} from "./useLiveRecorder";
import {
  clearLiveRecordingRecoveryManifest,
  listLiveRecordingRecoveryManifests,
  getLiveRecordingRecoveryForPlatform,
  getWebRecoveryFormat,
  getLiveRecordingRecoveryForTranscript,
  setLiveRecordingRecoveryManifest,
  touchLiveRecordingRecoveryManifest,
} from "./recovery";
import {
  getRecordingPlatform,
  getRecordingUiState,
  openRecordingAudioInput,
  recordRecordingStatus,
  reportRecordingStopped,
  useRecordingUiSelector,
} from "./ports";
import {
  attachRecordingBeforeUnloadListener,
  attachRecordingDeviceChangeListener,
  attachRecordingNetworkListeners,
  attachRecordingVisibilityListener,
} from "./lifecycle";

interface StoredAudioChunk {
  id: string;
  sessionId: string;
  sequenceNumber: number;
  timestamp: number;
  retryCount: number;
  byteLength: number;
  data: ArrayBuffer;
  createdAt: number;
}

interface TranscriptRequestResult {
  success: boolean;
  blocked: boolean;
  errorMessage?: string;
}

type LiveRecordingChunkInput = {
  transcriptId: string;
  action: "setFormat" | "addChunk" | "finalize" | "status";
  format?: {
    deviceType: DeviceType;
    sampleRate: number;
    channels: number;
    bitsPerSample: number;
  };
  chunk?: number[];
  sequenceNumber?: number;
};

type WebRecordingApi = {
  liveRecordingChunk: (
    input: LiveRecordingChunkInput,
    options?: { timeoutMs?: number },
  ) => Promise<ActionResult<unknown>>;
};

let _webRecordingApi: WebRecordingApi | undefined;

export const configureWebRecordingApi = (api: WebRecordingApi): void => {
  _webRecordingApi = api;
};

const _recordingApi = (): WebRecordingApi => {
  if (!_webRecordingApi) {
    throw new Error("Web recording API used before controller construction");
  }
  return _webRecordingApi;
};

const INITIAL_RETRY_DELAY_MS = 1000;
const MAX_RETRY_DELAY_MS = 30000;
const TARGET_SAMPLE_RATE = 16000;
const FLUSH_THRESHOLD_BYTES = 15 * TARGET_SAMPLE_RATE * 2;
// Cap how long stopRecording waits for the upload pipeline to drain. If we're
// still waiting after this, we abandon the wait and surface a save-failed
// error so the user can retry via the recovery flow on the next launch
// instead of staring at an indefinite spinner. 150s so the budget covers a
// first chunk attempt (45s) plus one escalated retry (90s) — a shorter budget
// surfaces spurious failures for saves that then complete in the background.
const STOP_UPLOAD_DRAIN_TIMEOUT_MS = 150 * 1000;
// Per-request caps so a TCP-level stall (Wi-Fi switch, laptop sleep/wake, a
// proxy silently dropping the connection) surfaces as a retryable failure
// instead of a fetch that hangs forever. navigator.onLine stays true in those
// cases, so without an abort the upload queue would stall indefinitely.
// Chunk uploads get a generous cap because a 15s audio chunk is ~1.9MB of
// JSON and must still complete on slow uplinks. The cap GROWS with each retry
// (up to a max) so a genuinely slow-but-working connection that cannot finish
// inside the base window is not aborted forever — a fixed cap would turn very
// slow uplinks into a permanent retry loop and eventual data loss once the
// local backup ages out.
const FORMAT_REQUEST_TIMEOUT_MS = 15 * 1000;
const CHUNK_REQUEST_TIMEOUT_MS = 45 * 1000;
const CHUNK_REQUEST_TIMEOUT_MAX_MS = 240 * 1000;
// Exhausted retries require explicit recovery; never skip recorded audio.
const MAX_CHUNK_UPLOAD_RETRIES = 50;
const STALE_RECORDING_MESSAGE =
  "This recording is more than 10 hours old and can no longer accept audio. Audio is backed up on this device.";
// Silence handling: long thinking pauses are a normal part of dictation and
// must NEVER stop or interrupt the recording. We only surface a dismissible
// warning banner, and only when the input is essentially digital zero (a live
// mic in a quiet room still has a noise floor well above this threshold —
// flat zero means the mic is muted at the OS level, dead, or disconnected),
// sustained for a long grace period.
const SILENCE_WARNING_LEVEL = 0.001;
const SILENCE_WARNING_AFTER_MS = 120 * 1000;
const SILENCE_WARNING_MESSAGE =
  "No audio has been detected for a while. Check that the microphone is not muted or disconnected. The recording is still running.";
const MIC_LOST_WARNING_MESSAGE =
  "The microphone was disconnected. Reconnect a microphone to keep recording — audio captured so far is safe.";
const LOW_STORAGE_WARNING_MESSAGE =
  "This device is running low on storage. The local recording backup may stop working soon.";
// ~200MB ≈ 1.75h of 16kHz/16-bit audio headroom before the local backup
// starts failing writes.
const LOW_STORAGE_HEADROOM_BYTES = 200 * 1024 * 1024;
const LIVE_RECORDING_DB_NAME = "live-recording-backup";
const LIVE_RECORDING_DB_VERSION = 1;
const LIVE_RECORDING_CHUNKS_STORE = "chunks";
const LIVE_RECORDING_SESSION_INDEX = "sessionId";
const LIVE_RECORDING_SESSION_SEQUENCE_INDEX = "sessionSequence";
const LIVE_RECORDING_CREATED_AT_INDEX = "createdAt";
const STALE_CHUNK_MAX_AGE_MS = 48 * 60 * 60 * 1000;

let liveRecordingDbPromise: Promise<IDBDatabase> | null = null;

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("IndexedDB request failed"));
  });
}

const transactionToPromise = (transaction: IDBTransaction) =>
  new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(transaction.error ?? new Error("IndexedDB transaction failed"));
    transaction.onabort = () =>
      reject(transaction.error ?? new Error("IndexedDB transaction aborted"));
  });

const iterateCursor = (
  request: IDBRequest<IDBCursorWithValue | null>,
  onCursor: (cursor: IDBCursorWithValue) => void,
) =>
  new Promise<void>((resolve, reject) => {
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) {
        resolve();
        return;
      }

      onCursor(cursor);
    };

    request.onerror = () =>
      reject(request.error ?? new Error("IndexedDB cursor failed"));
  });

const openLiveRecordingDb = async () => {
  if (typeof indexedDB === "undefined") {
    throw new Error("IndexedDB is unavailable in this browser");
  }

  if (!liveRecordingDbPromise) {
    const pending = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(
        LIVE_RECORDING_DB_NAME,
        LIVE_RECORDING_DB_VERSION,
      );

      request.onupgradeneeded = () => {
        const database = request.result;
        let store: IDBObjectStore;

        if (!database.objectStoreNames.contains(LIVE_RECORDING_CHUNKS_STORE)) {
          store = database.createObjectStore(LIVE_RECORDING_CHUNKS_STORE, {
            keyPath: "id",
          });
        } else {
          store = request.transaction!.objectStore(LIVE_RECORDING_CHUNKS_STORE);
        }

        if (!store.indexNames.contains(LIVE_RECORDING_SESSION_INDEX)) {
          store.createIndex(LIVE_RECORDING_SESSION_INDEX, "sessionId", {
            unique: false,
          });
        }

        if (!store.indexNames.contains(LIVE_RECORDING_SESSION_SEQUENCE_INDEX)) {
          store.createIndex(
            LIVE_RECORDING_SESSION_SEQUENCE_INDEX,
            ["sessionId", "sequenceNumber"],
            { unique: true },
          );
        }

        if (!store.indexNames.contains(LIVE_RECORDING_CREATED_AT_INDEX)) {
          store.createIndex(LIVE_RECORDING_CREATED_AT_INDEX, "createdAt", {
            unique: false,
          });
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () =>
        reject(request.error ?? new Error("Failed to open IndexedDB"));
      request.onblocked = () =>
        reject(new Error("IndexedDB is blocked by another tab"));
    });

    // If the open fails (transient lock, blocked by another tab, quota
    // exceeded, etc.) clear the cached promise so the next call gets a fresh
    // attempt. Otherwise the entire local-backup pipeline stays poisoned for
    // the rest of the session.
    pending.catch(() => {
      if (liveRecordingDbPromise === pending) {
        liveRecordingDbPromise = null;
      }
    });

    liveRecordingDbPromise = pending;
  }

  return liveRecordingDbPromise;
};

const putStoredChunk = async (chunk: StoredAudioChunk) => {
  const database = await openLiveRecordingDb();
  const transaction = database.transaction(
    LIVE_RECORDING_CHUNKS_STORE,
    "readwrite",
  );
  const store = transaction.objectStore(LIVE_RECORDING_CHUNKS_STORE);
  store.put(chunk);
  await transactionToPromise(transaction);
};

const getNextStoredChunk = async (sessionId: string) => {
  const database = await openLiveRecordingDb();
  const transaction = database.transaction(
    LIVE_RECORDING_CHUNKS_STORE,
    "readonly",
  );
  const index = transaction
    .objectStore(LIVE_RECORDING_CHUNKS_STORE)
    .index(LIVE_RECORDING_SESSION_SEQUENCE_INDEX);
  const range = IDBKeyRange.bound(
    [sessionId, 0],
    [sessionId, Number.MAX_SAFE_INTEGER],
  );
  const cursor = await requestToPromise<IDBCursorWithValue | null>(
    index.openCursor(range),
  );
  const chunk = (cursor?.value as StoredAudioChunk | undefined) ?? null;
  await transactionToPromise(transaction);
  return chunk;
};

const updateStoredChunkRetry = async (chunkId: string, retryCount: number) => {
  const database = await openLiveRecordingDb();
  const transaction = database.transaction(
    LIVE_RECORDING_CHUNKS_STORE,
    "readwrite",
  );
  const store = transaction.objectStore(LIVE_RECORDING_CHUNKS_STORE);
  const existing = await requestToPromise<StoredAudioChunk | undefined>(
    store.get(chunkId),
  );

  if (existing) {
    store.put({ ...existing, retryCount });
  }

  await transactionToPromise(transaction);
};

const deleteStoredChunk = async (chunkId: string) => {
  const database = await openLiveRecordingDb();
  const transaction = database.transaction(
    LIVE_RECORDING_CHUNKS_STORE,
    "readwrite",
  );
  transaction.objectStore(LIVE_RECORDING_CHUNKS_STORE).delete(chunkId);
  await transactionToPromise(transaction);
};

const clearSessionChunks = async (sessionId: string) => {
  const database = await openLiveRecordingDb();
  const transaction = database.transaction(
    LIVE_RECORDING_CHUNKS_STORE,
    "readwrite",
  );
  const index = transaction
    .objectStore(LIVE_RECORDING_CHUNKS_STORE)
    .index(LIVE_RECORDING_SESSION_INDEX);

  await iterateCursor(
    index.openCursor(IDBKeyRange.only(sessionId)),
    (cursor) => {
      cursor.delete();
      cursor.continue();
    },
  );

  await transactionToPromise(transaction);
};

const getSessionStats = async (sessionId: string) => {
  const database = await openLiveRecordingDb();
  const transaction = database.transaction(
    LIVE_RECORDING_CHUNKS_STORE,
    "readonly",
  );
  const index = transaction
    .objectStore(LIVE_RECORDING_CHUNKS_STORE)
    .index(LIVE_RECORDING_SESSION_INDEX);

  let pendingChunks = 0;
  let pendingBytes = 0;
  let maxSequenceNumber = 0;

  await iterateCursor(
    index.openCursor(IDBKeyRange.only(sessionId)),
    (cursor) => {
      const chunk = cursor.value as StoredAudioChunk;
      pendingChunks += 1;
      pendingBytes += chunk.byteLength;
      maxSequenceNumber = Math.max(maxSequenceNumber, chunk.sequenceNumber);
      cursor.continue();
    },
  );

  await transactionToPromise(transaction);

  return { pendingChunks, pendingBytes, maxSequenceNumber };
};

const purgeStaleChunks = async (maxAgeMs: number = STALE_CHUNK_MAX_AGE_MS) => {
  const recoverable = new Set((await listLiveRecordingRecoveryManifests()).map(manifest => manifest.transcriptId));
  const database = await openLiveRecordingDb();
  const transaction = database.transaction(
    LIVE_RECORDING_CHUNKS_STORE,
    "readwrite",
  );
  const cutoff = Date.now() - maxAgeMs;

  await iterateCursor(
    transaction.objectStore(LIVE_RECORDING_CHUNKS_STORE).openCursor(),
    (cursor) => {
      const chunk = cursor.value as StoredAudioChunk;
      if (chunk.createdAt < cutoff && !recoverable.has(chunk.sessionId)) {
        cursor.delete();
      }
      cursor.continue();
    },
  );

  await transactionToPromise(transaction);
};

export const useLiveRecorderWeb = () => {
  const status = useRecordingUiSelector(
    (state) => state.liveRecording?.status || "inactive",
  );
  const [elapsedTime, setElapsedTime] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [recordingHealth, setRecordingHealth] = useState<RecordingHealth>({
    status: "healthy",
    pendingChunks: 0,
    hasNetworkError: false,
    isNetworkAvailable: true,
    lastChunkSentAt: null,
    isServerReady: true,
    isLocalBackupReady: true,
    hasDataLoss: false,
    droppedChunks: 0,
    failedChunks: 0,
    lastErrorMessage: null,
  });

  const startTimeRef = useRef<number | null>(null);
  const durationRef = useRef<number>(0);
  const pauseTransitionRef = useRef<Promise<void> | undefined>(undefined);
  const timerIntervalRef = useRef<NodeJS.Timeout>(null);
  const lastChunkSentAtRef = useRef<number | null>(null);

  const audioContextRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const releaseInputRef = useRef<(() => void) | undefined>(undefined);
  const releaseSessionLockRef = useRef<(() => void) | undefined>(undefined);
  const acquireSessionLock = async (transcriptId: string) => {
    if (!navigator.locks || releaseSessionLockRef.current) return;
    await new Promise<void>((resolve, reject) => {
      void navigator.locks.request(`recording:${transcriptId}`, { ifAvailable: true }, async lock => {
        if (!lock) { reject(new Error("Recording is active in another tab")); return; }
        await new Promise<void>(release => { releaseSessionLockRef.current = release; resolve(); });
      }).catch(reject);
    });
  };
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const audioWorkletNodeRef = useRef<AudioWorkletNode | null>(null);
  const workletMessageHandlerRef = useRef<
    ((event: MessageEvent) => void) | null
  >(null);
  const workletFlushCompletionRef = useRef<{
    resolve: () => void;
    reject: (error: Error) => void;
  } | null>(null);
  const isRecordingRef = useRef(false);

  const audioSegmentsRef = useRef<Int16Array[]>([]);
  const audioSegmentsTotalSamplesRef = useRef(0);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  const audioFormatRef = useRef<{
    sampleRate?: number;
    channels?: number;
    bitsPerSample?: number;
    originalSampleRate?: number;
  }>({});

  const sessionIdRef = useRef<string | null>(null);
  const pendingChunkCountRef = useRef(0);
  const pendingBytesRef = useRef(0);
  const uploadedBytesRef = useRef(0);
  const chunkSequenceNumberRef = useRef(0);
  const isProcessingQueueRef = useRef(false);
  const isSendingFormatRef = useRef(false);
  const hasNetworkErrorRef = useRef(false);
  const formatSentRef = useRef(false);
  const stopRequestedRef = useRef(false);
  const localBackupReadyRef = useRef(true);
  const hasDataLossRef = useRef(false);
  const droppedChunksRef = useRef(0);
  const failedChunksRef = useRef(0);
  const lastErrorMessageRef = useRef<string | null>(null);
  const lastUploadFailureWasFatalRef = useRef(false);
  const uploadBlockedRef = useRef(false);
  const activePersistenceCountRef = useRef(0);

  const silenceStartedAtRef = useRef<number | null>(null);
  const isSilencedRef = useRef(false);
  const micLostRef = useRef(false);
  const micRecoveryInFlightRef = useRef(false);
  const lastMicRecoveryAttemptAtRef = useRef(0);
  const deviceChangeHandlerRef = useRef<(() => void) | null>(null);
  const storageLowRef = useRef(false);

  const retryTimerRef = useRef<NodeJS.Timeout | null>(null);
  const formatRetryCountRef = useRef(0);
  const formatRetryTimerRef = useRef<NodeJS.Timeout | null>(null);
  const startPromiseRef = useRef<Promise<boolean> | null>(null);
  const cancelPendingStartRef = useRef(false);
  const stopCompletionRef = useRef<{
    resolve: () => void;
    reject: (error: StopRecordingError) => void;
  } | null>(null);
  const stopPromiseRef = useRef<Promise<void> | null>(null);
  const stopPollingRef = useRef<(() => void) | null>(null);

  const statusRef = useRef(status);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const lastAudioLevelUpdateRef = useRef(0);
  const AUDIO_LEVEL_THROTTLE_MS = 100;

  const int16ArrayToUint8Array = (int16Array: Int16Array): Uint8Array => {
    const byteArray = new Uint8Array(int16Array.length * 2);
    const dataView = new DataView(byteArray.buffer);
    for (let i = 0; i < int16Array.length; i++) {
      dataView.setInt16(i * 2, int16Array[i], true);
    }
    return byteArray;
  };

  const downsampleAudio = (
    audioData: Int16Array,
    originalSampleRate: number,
    targetSampleRate: number,
  ): Int16Array => {
    if (originalSampleRate === targetSampleRate) {
      // Return a copy so downstream mutations (e.g. reduceBitDepth) do not
      // modify the input buffer the worklet still owns.
      return new Int16Array(audioData);
    }

    const ratio = originalSampleRate / targetSampleRate;
    const newLength = Math.floor(audioData.length / ratio);
    const result = new Int16Array(newLength);

    for (let i = 0; i < newLength; i++) {
      const sourceIndex = Math.floor(i * ratio);
      let sum = 0;
      let count = 0;
      const windowSize = Math.min(Math.ceil(ratio), 8);

      for (let j = 0; j < windowSize; j++) {
        const index = sourceIndex + j;
        if (index < audioData.length) {
          sum += audioData[index];
          count++;
        }
      }

      result[i] = count > 0 ? Math.round(sum / count) : 0;
    }

    return result;
  };

  const reduceBitDepth = (audioData: Int16Array): Int16Array => {
    for (let i = 0; i < audioData.length; i++) {
      audioData[i] = audioData[i] & 0xfffc;
    }
    return audioData;
  };

  const isNetworkAvailable = () => navigator.onLine;

  const requestWakeLock = async () => {
    if (!("wakeLock" in navigator)) {
      return;
    }

    try {
      wakeLockRef.current = await navigator.wakeLock.request("screen");
    } catch (error) {
      console.error("Failed to acquire wake lock:", error);
    }
  };

  const releaseWakeLock = async () => {
    if (!wakeLockRef.current) {
      return;
    }

    try {
      await wakeLockRef.current.release();
    } catch (error) {
      console.error("Failed to release wake lock:", error);
    } finally {
      wakeLockRef.current = null;
    }
  };

  const buildStopRecordingError = (
    source?: Partial<StopRecordingError> & { message?: string },
  ): StopRecordingError => ({
    message:
      source?.message ??
      lastErrorMessageRef.current ??
      "The recording could not be saved reliably.",
    pendingChunks: source?.pendingChunks ?? pendingChunkCountRef.current,
    pendingBytes: source?.pendingBytes ?? pendingBytesRef.current,
    isServerReady: source?.isServerReady ?? formatSentRef.current,
    isLocalBackupReady:
      source?.isLocalBackupReady ?? localBackupReadyRef.current,
    hasDataLoss: source?.hasDataLoss ?? hasDataLossRef.current,
    droppedChunks: source?.droppedChunks ?? droppedChunksRef.current,
    failedChunks: source?.failedChunks ?? failedChunksRef.current,
  });

  const getRecordingHealthStatus = (): RecordingHealthStatus => {
    const networkAvailable = isNetworkAvailable();
    let healthStatus: RecordingHealthStatus = "healthy";

    if (!localBackupReadyRef.current || hasDataLossRef.current) {
      healthStatus = "error";
    } else if (
      hasNetworkErrorRef.current ||
      !networkAvailable ||
      pendingBytesRef.current >= FLUSH_THRESHOLD_BYTES ||
      stopRequestedRef.current ||
      isSilencedRef.current ||
      micLostRef.current ||
      storageLowRef.current
    ) {
      healthStatus = "warning";
    }

    return healthStatus;
  };

  const updateRecordingHealth = () => {
    const networkAvailable = isNetworkAvailable();
    const healthStatus = getRecordingHealthStatus();

    setRecordingHealth({
      status: healthStatus,
      pendingChunks: pendingChunkCountRef.current,
      pendingBytes: pendingBytesRef.current,
      hasNetworkError: hasNetworkErrorRef.current,
      isNetworkAvailable: networkAvailable,
      lastChunkSentAt: lastChunkSentAtRef.current,
      isServerReady: formatSentRef.current,
      isLocalBackupReady: localBackupReadyRef.current,
      hasDataLoss: hasDataLossRef.current,
      droppedChunks: droppedChunksRef.current,
      failedChunks: failedChunksRef.current,
      lastErrorMessage: lastErrorMessageRef.current,
      isSilenced: isSilencedRef.current,
    });
  };

  // The active advisory (mic lost > silence > low storage) derived from the
  // condition refs, not from stored text. setLastErrorMessage falls back to
  // this whenever the message would go null, so routine clears elsewhere in
  // the pipeline (e.g. after each successful chunk upload) cannot wipe a
  // banner whose underlying condition still holds.
  const getActiveAdvisoryMessage = (): string | null => {
    if (micLostRef.current) {
      return MIC_LOST_WARNING_MESSAGE;
    }
    if (isSilencedRef.current) {
      return SILENCE_WARNING_MESSAGE;
    }
    if (storageLowRef.current) {
      return LOW_STORAGE_WARNING_MESSAGE;
    }
    return null;
  };

  const setLastErrorMessage = (message: string | null) => {
    lastErrorMessageRef.current = message ?? getActiveAdvisoryMessage();
    updateRecordingHealth();
  };

  // Advisory warnings (silence / mic lost / low storage) must never clobber a
  // more important error message, and clearing one must not wipe another's
  // message. Callers flip the condition ref BEFORE calling these, so the
  // null-fallback in setLastErrorMessage resolves overlaps automatically.
  const setAdvisoryWarning = (message: string) => {
    if (
      !lastErrorMessageRef.current ||
      lastErrorMessageRef.current === getActiveAdvisoryMessage() ||
      lastErrorMessageRef.current === SILENCE_WARNING_MESSAGE ||
      lastErrorMessageRef.current === LOW_STORAGE_WARNING_MESSAGE
    ) {
      // Display the highest-priority active advisory, not necessarily the
      // one that fired — a dead mic also trips the silence detector, and
      // "mic disconnected" must not be replaced by the weaker message.
      setLastErrorMessage(getActiveAdvisoryMessage() ?? message);
    } else {
      updateRecordingHealth();
    }
  };

  const clearAdvisoryWarning = (message: string) => {
    if (lastErrorMessageRef.current === message) {
      setLastErrorMessage(null);
    } else {
      updateRecordingHealth();
    }
  };

  const resetSilenceTracking = () => {
    silenceStartedAtRef.current = null;
    if (isSilencedRef.current) {
      isSilencedRef.current = false;
      clearAdvisoryWarning(SILENCE_WARNING_MESSAGE);
    }
  };

  // Long pauses are normal — the user may simply be thinking. This never
  // stops or interrupts the recording; it only raises a warning banner after
  // a long stretch of essentially-zero input (muted or dead mic) and clears
  // it the moment audio returns.
  const trackSilence = (audioLevel: number) => {
    if (!isRecordingRef.current || stopRequestedRef.current) {
      return;
    }

    if (audioLevel >= SILENCE_WARNING_LEVEL) {
      resetSilenceTracking();
      return;
    }

    if (silenceStartedAtRef.current === null) {
      silenceStartedAtRef.current = Date.now();
      return;
    }

    if (
      !isSilencedRef.current &&
      Date.now() - silenceStartedAtRef.current >= SILENCE_WARNING_AFTER_MS
    ) {
      isSilencedRef.current = true;
      setAdvisoryWarning(SILENCE_WARNING_MESSAGE);
    }
  };

  const handleMicTrackEnded = () => {
    if (stopRequestedRef.current || !sessionIdRef.current) {
      return;
    }

    micLostRef.current = true;
    setAdvisoryWarning(MIC_LOST_WARNING_MESSAGE);
    void recoverMicrophone();
  };

  // Try to transparently re-acquire the microphone after the track ended
  // (Bluetooth headset off, USB mic unplugged, OS revoked the device). The
  // permission is already granted, so this normally succeeds without a
  // prompt. Failure keeps the session alive with a warning — retried on
  // devicechange, on tab visibility, and on resume.
  const recoverMicrophone = async () => {
    if (
      micRecoveryInFlightRef.current ||
      !micLostRef.current ||
      stopRequestedRef.current ||
      !sessionIdRef.current ||
      // While paused there is nothing to capture; resumeRecording retries.
      !isRecordingRef.current ||
      // Backoff against a pathological device that ends its track right
      // after being acquired (onended → recover → onended → ...). Retries
      // stay event-driven (devicechange / visibility / resume).
      Date.now() - lastMicRecoveryAttemptAtRef.current < 2000
    ) {
      return;
    }

    micRecoveryInFlightRef.current = true;
    lastMicRecoveryAttemptAtRef.current = Date.now();
    const sessionIdAtStart = sessionIdRef.current;

    try {
      const { stream, release } = await openRecordingAudioInput();

      const audioContext = audioContextRef.current;
      const workletNode = audioWorkletNodeRef.current;

      // The session may have ended — or a NEW session may have started —
      // while we were acquiring the device. Never touch another session's
      // healthy audio graph.
      if (
        stopRequestedRef.current ||
        !isRecordingRef.current ||
        sessionIdRef.current !== sessionIdAtStart ||
        !audioContext ||
        audioContext.state === "closed" ||
        !workletNode
      ) {
        release();
        return;
      }

      if (streamRef.current) {
        releaseInputRef.current?.();
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      if (sourceRef.current) {
        try {
          sourceRef.current.disconnect();
        } catch {
          // Old source may already be detached.
        }
      }

      streamRef.current = stream;
      releaseInputRef.current = release;
      sourceRef.current = audioContext.createMediaStreamSource(stream);
      sourceRef.current.connect(workletNode);
      stream.getTracks().forEach((track) => {
        track.onended = handleMicTrackEnded;
      });

      if (audioContext.state === "suspended") {
        try {
          await audioContext.resume();
        } catch (error) {
          console.error(
            "Failed to resume audio context after mic recovery:",
            error,
          );
        }
      }

      micLostRef.current = false;
      silenceStartedAtRef.current = null;
      clearAdvisoryWarning(MIC_LOST_WARNING_MESSAGE);
    } catch (error) {
      console.error("Failed to recover the microphone:", error);
      updateRecordingHealth();
    } finally {
      micRecoveryInFlightRef.current = false;
    }
  };

  // Mobile Safari suspends the AudioContext on phone calls / Siri / screen
  // lock and does not always resume it on its own. Auto-resume whenever the
  // session is supposed to be capturing. isRecordingRef is false during a
  // user-initiated pause and during stop, so this never fights those flows.
  const handleAudioContextStateChange = () => {
    const audioContext = audioContextRef.current;
    if (!audioContext) {
      return;
    }

    const state = audioContext.state as string;
    if (
      (state === "suspended" || state === "interrupted") &&
      isRecordingRef.current &&
      !stopRequestedRef.current
    ) {
      audioContext.resume().catch((error) => {
        // Expected while an OS-level interruption (e.g. phone call) is still
        // active; retried on the next statechange and on visibilitychange.
        console.error("Failed to auto-resume audio context:", error);
      });
    }
  };

  const checkStorageHeadroom = async () => {
    try {
      if (!navigator.storage?.estimate) {
        return;
      }

      const { quota, usage } = await navigator.storage.estimate();
      if (!quota) {
        return;
      }

      const headroom = quota - (usage ?? 0);
      if (headroom < LOW_STORAGE_HEADROOM_BYTES) {
        if (!storageLowRef.current) {
          storageLowRef.current = true;
          setAdvisoryWarning(LOW_STORAGE_WARNING_MESSAGE);
        }
      } else if (storageLowRef.current) {
        storageLowRef.current = false;
        clearAdvisoryWarning(LOW_STORAGE_WARNING_MESSAGE);
      }
    } catch {
      // storage.estimate is best-effort; never disturb the recording.
    }
  };

  const setCurrentLiveRecordingStatus = (nextStatus: LiveRecordingStatus) => {
    const currentLiveRecording = getRecordingUiState().liveRecording;
    if (!currentLiveRecording?.itemId) {
      return false;
    }

    getRecordingUiState().setLiveRecording({
      ...currentLiveRecording,
      status: nextStatus,
      recordingStartedAt:
        startTimeRef.current === null
          ? undefined
          : startTimeRef.current - durationRef.current,
      recordingElapsedMs:
        startTimeRef.current === null ? durationRef.current : undefined,
    });

    return true;
  };

  const notifyStatusChange = (
    newStatus: LiveRecordingStatus,
    message?: string,
  ) => {
    // If liveRecording was cleared (e.g. user cancelled during startup),
    // don't resurrect it. Spreading undefined creates { status } with no
    // itemId, producing a ghost recording with no UI and no cleanup path.
    if (!setCurrentLiveRecordingStatus(newStatus)) {
      return;
    }

    recordRecordingStatus({
      status: newStatus,
      healthStatus: getRecordingHealthStatus(),
      pendingChunks: pendingChunkCountRef.current,
      pendingBytes: pendingBytesRef.current,
      isLocalBackupReady: localBackupReadyRef.current,
      hasDataLoss: hasDataLossRef.current,
      droppedChunks: droppedChunksRef.current,
      failedChunks: failedChunksRef.current,
    });

    console.log("Live recording status:", {
      status: newStatus,
      message,
      pendingChunks: pendingChunkCountRef.current,
      pendingBytes: pendingBytesRef.current,
      localBackupReady: localBackupReadyRef.current,
      hasDataLoss: hasDataLossRef.current,
    });

    const transcriptId = sessionIdRef.current;
    if (
      (newStatus === "recording" || newStatus === "uploading") &&
      transcriptId
    ) {
      void touchLiveRecordingRecoveryManifest(transcriptId, {
        status: newStatus === "uploading" ? "uploading" : "recording",
        lastErrorMessage: null,
      });
    }

    updateRecordingHealth();
  };

  const rejectPendingStop = (error: StopRecordingError) => {
    if (!stopCompletionRef.current) {
      return;
    }

    stopCompletionRef.current.reject(error);
    stopCompletionRef.current = null;
  };

  const resolvePendingStop = () => {
    if (!stopCompletionRef.current) {
      return;
    }

    stopCompletionRef.current.resolve();
    stopCompletionRef.current = null;
  };

  const clearRetryTimer = () => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  };

  const clearFormatRetryTimer = () => {
    if (formatRetryTimerRef.current) {
      clearTimeout(formatRetryTimerRef.current);
      formatRetryTimerRef.current = null;
    }
  };

  const stopPolling = () => {
    if (stopPollingRef.current) {
      stopPollingRef.current();
      stopPollingRef.current = null;
    }
  };

  const setupChunkProcessing = (chunkIntervalSeconds: number = 15) => {
    // Always stop any prior polling loop before starting a new one - otherwise
    // rapid pause/resume cycles can spawn duplicate loops that race on flush.
    stopPolling();

    let pollingShouldStop = false;

    const stopFn = () => {
      pollingShouldStop = true;
    };
    stopPollingRef.current = stopFn;

    const poll = async () => {
      while (!pollingShouldStop) {
        if (
          isRecordingRef.current &&
          audioSegmentsTotalSamplesRef.current > 0
        ) {
          await flushAudioBuffer();
        }

        await new Promise((resolve) =>
          setTimeout(resolve, chunkIntervalSeconds * 1000),
        );
      }
    };

    void poll();
  };

  const sendTranscriptRequest = async (
    payload: LiveRecordingChunkInput,
    options?: { timeoutMs?: number },
  ): Promise<TranscriptRequestResult> => {
    try {
      const result = await _recordingApi().liveRecordingChunk(payload, options);
      if (!("error" in result)) {
        return { success: true, blocked: false };
      }

      if (result.error === "unauthorized") {
        return {
          success: false,
          blocked: false,
          errorMessage:
            "Upload is waiting for sign-in. Audio is backed up on this device.",
        };
      }

      if (result.error === "AudioDurationTooLong") {
        return {
          success: false,
          blocked: true,
          errorMessage: STALE_RECORDING_MESSAGE,
        };
      }

      const blocked = ![
        "networkUnavailable",
        "timeout",
        "cancelled",
        "RateLimitExceeded",
        "Internal",
      ].includes(result.error);
      return {
        success: false,
        blocked,
        errorMessage: blocked
          ? "This recording upload is blocked by the server. Audio is backed up on this device."
          : "The server did not accept the audio upload.",
      };
    } catch {
      return {
        success: false,
        blocked: false,
        errorMessage: "Audio upload is waiting for a stable connection.",
      };
    }
  };

  const finalizeStopIfReady = () => {
    if (!stopRequestedRef.current) {
      return;
    }

    if (uploadBlockedRef.current) {
      rejectPendingStop(
        buildStopRecordingError({
          message:
            lastErrorMessageRef.current ||
            "This recording upload is blocked. Audio remains backed up on this device.",
          pendingChunks: pendingChunkCountRef.current,
          pendingBytes: pendingBytesRef.current,
          isLocalBackupReady: localBackupReadyRef.current,
        }),
      );
      return;
    }

    if (!localBackupReadyRef.current || hasDataLossRef.current) {
      rejectPendingStop(buildStopRecordingError());
      return;
    }

    if (activePersistenceCountRef.current > 0) {
      return;
    }

    if (audioSegmentsTotalSamplesRef.current > 0) {
      return;
    }

    if (!formatSentRef.current) {
      return;
    }

    if (pendingChunkCountRef.current > 0 || isProcessingQueueRef.current) {
      // Only kick the queue when no retry backoff is pending. When the queue
      // just failed (offline / server error) it arms a retry timer and its
      // finally-block re-enters here — kicking again immediately would bypass
      // the backoff and busy-loop: kick → fail instantly → schedule → kick...
      if (
        !isProcessingQueueRef.current &&
        !retryTimerRef.current &&
        !formatRetryTimerRef.current
      ) {
        void processChunkQueue();
      }
      return;
    }

    stopRequestedRef.current = false;
    setLastErrorMessage(null);
    notifyStatusChange("stopped");
    resolvePendingStop();
  };

  const failRecording = async (
    message: string,
    options?: { localBackupFailed?: boolean },
  ) => {
    hasDataLossRef.current = true;
    failedChunksRef.current += 1;

    if (options?.localBackupFailed) {
      localBackupReadyRef.current = false;
    }

    setLastErrorMessage(message);
    isRecordingRef.current = false;

    stopPolling();
    await releaseWakeLock();
    await stopCaptureOnly();

    notifyStatusChange("error", message);
    rejectPendingStop(buildStopRecordingError({ message }));
  };

  const persistBlockedRecoveryState = async (message: string) => {
    const transcriptId = sessionIdRef.current;
    if (!transcriptId) {
      return;
    }

    const manifest = await getLiveRecordingRecoveryForTranscript(transcriptId);
    await setLiveRecordingRecoveryManifest({
      platform: "web",
      transcriptId,
      status: "blocked",
      startedAt: manifest?.startedAt ?? startTimeRef.current ?? Date.now(),
      uploadedBytes: uploadedBytesRef.current,
      lastErrorMessage: message,
    });
  };

  const blockRecording = async (message: string) => {
    uploadBlockedRef.current = true;
    lastUploadFailureWasFatalRef.current = true;
    hasNetworkErrorRef.current = false;
    setLastErrorMessage(message);
    clearRetryTimer();
    clearFormatRetryTimer();
    stopPolling();
    await releaseWakeLock();
    await stopCaptureOnly();
    await persistBlockedRecoveryState(message);
    notifyStatusChange("error", message);
    rejectPendingStop(
      buildStopRecordingError({
        message,
        pendingChunks: pendingChunkCountRef.current,
        pendingBytes: pendingBytesRef.current,
        isLocalBackupReady: localBackupReadyRef.current,
      }),
    );
  };

  const scheduleRetry = async (chunk?: StoredAudioChunk) => {
    if (uploadBlockedRef.current) {
      return;
    }

    if (chunk) {
      const nextRetryCount = chunk.retryCount + 1;
      await updateStoredChunkRetry(chunk.id, nextRetryCount);
      chunk.retryCount = nextRetryCount;
    }

    const retryCount = chunk?.retryCount ?? 1;
    const delay = Math.min(
      INITIAL_RETRY_DELAY_MS * Math.pow(2, Math.min(retryCount - 1, 5)),
      MAX_RETRY_DELAY_MS,
    );

    clearRetryTimer();
    retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null;
      void processChunkQueue();
    }, delay);
  };

  const scheduleFormatRetry = () => {
    if (uploadBlockedRef.current) {
      return;
    }

    formatRetryCountRef.current += 1;
    const delay = Math.min(
      INITIAL_RETRY_DELAY_MS *
        Math.pow(2, Math.min(formatRetryCountRef.current - 1, 5)),
      MAX_RETRY_DELAY_MS,
    );

    clearFormatRetryTimer();
    formatRetryTimerRef.current = setTimeout(() => {
      formatRetryTimerRef.current = null;
      void sendFormatToServer();
    }, delay);
  };

  const sendFormatToServer = async (): Promise<boolean> => {
    if (formatSentRef.current || isSendingFormatRef.current) {
      return formatSentRef.current;
    }

    const format = audioFormatRef.current;
    const transcriptId = sessionIdRef.current;
    if (!format.sampleRate || !transcriptId) {
      return false;
    }

    isSendingFormatRef.current = true;

    try {
      const result = await sendTranscriptRequest(
        {
          action: "setFormat",
          transcriptId,
          format: {
            deviceType: getRecordingPlatform(),
            sampleRate: format.sampleRate,
            channels: format.channels || 1,
            bitsPerSample: format.bitsPerSample || 16,
          },
        },
        { timeoutMs: FORMAT_REQUEST_TIMEOUT_MS },
      );

      if (result.success) {
        formatSentRef.current = true;
        formatRetryCountRef.current = 0;
        hasNetworkErrorRef.current = false;
        uploadBlockedRef.current = false;
        lastUploadFailureWasFatalRef.current = false;
        clearFormatRetryTimer();

        if (
          pendingBytesRef.current < FLUSH_THRESHOLD_BYTES &&
          !stopRequestedRef.current
        ) {
          setLastErrorMessage(null);
        }

        updateRecordingHealth();

        // Kick the queue so chunks drain immediately after a format retry
        // succeeds. Without this the drain waits for the 5s watchdog interval,
        // which needlessly extends the stop spinner. The re-entrancy guard in
        // processChunkQueue makes this a no-op when we're already inside it.
        if (pendingChunkCountRef.current > 0 || stopRequestedRef.current) {
          void processChunkQueue();
        }
        return true;
      }

      lastUploadFailureWasFatalRef.current = result.blocked;

      if (result.blocked) {
        await blockRecording(
          result.errorMessage ||
            "This recording upload is blocked by the server. Audio is backed up on this device.",
        );
        return false;
      }

      hasNetworkErrorRef.current = true;
      if (
        pendingBytesRef.current >= FLUSH_THRESHOLD_BYTES ||
        stopRequestedRef.current
      ) {
        setLastErrorMessage(
          "Upload is waiting for connection. Audio is backed up on this device.",
        );
      }
      updateRecordingHealth();
      scheduleFormatRetry();
      return false;
    } finally {
      isSendingFormatRef.current = false;
    }
  };

  const persistChunkToLocalBackup = async (chunkData: Uint8Array) => {
    if (!chunkData.length) {
      return;
    }

    const sessionId = sessionIdRef.current;
    if (!sessionId) {
      await failRecording(
        "The browser could not create the local recording backup.",
        { localBackupFailed: true },
      );
      return;
    }

    activePersistenceCountRef.current += 1;

    try {
      const sequenceNumber = ++chunkSequenceNumberRef.current;
      const chunk: StoredAudioChunk = {
        id: `${sessionId}:${sequenceNumber}`,
        sessionId,
        sequenceNumber,
        timestamp: Date.now(),
        retryCount: 0,
        byteLength: chunkData.byteLength,
        data: chunkData.buffer.slice(
          chunkData.byteOffset,
          chunkData.byteOffset + chunkData.byteLength,
        ) as ArrayBuffer,
        createdAt: Date.now(),
      };

      await putStoredChunk(chunk);
      pendingChunkCountRef.current += 1;
      pendingBytesRef.current += chunk.byteLength;
      updateRecordingHealth();
      void processChunkQueue();
    } catch (error) {
      console.error("Failed to persist chunk to IndexedDB:", error);
      await failRecording(
        "The browser could not save the recording locally. Stop and restart the recording.",
        { localBackupFailed: true },
      );
    } finally {
      activePersistenceCountRef.current -= 1;
      finalizeStopIfReady();
    }
  };

  const flushAudioBuffer = async () => {
    const segments = audioSegmentsRef.current;
    const totalSamples = audioSegmentsTotalSamplesRef.current;
    if (segments.length === 0 || totalSamples === 0) {
      return;
    }

    const merged = new Int16Array(totalSamples);
    let offset = 0;
    for (const segment of segments) {
      merged.set(segment, offset);
      offset += segment.length;
    }

    audioSegmentsRef.current = [];
    audioSegmentsTotalSamplesRef.current = 0;

    const compactChunk = int16ArrayToUint8Array(merged);
    // Piggyback the storage-headroom check on the ~15s flush cadence so we
    // warn before IndexedDB writes start failing, not after.
    void checkStorageHeadroom();
    await persistChunkToLocalBackup(compactChunk);
  };

  const requestWorkletFlush = async () => {
    if (!audioWorkletNodeRef.current) {
      return;
    }

    await new Promise<void>((resolve, reject) => {
      const completion = {
        resolve: () => {
          clearTimeout(timeout);
          resolve();
        },
        reject: (error: Error) => {
          clearTimeout(timeout);
          reject(error);
        },
      };

      const timeout = setTimeout(() => {
        if (workletFlushCompletionRef.current === completion) {
          workletFlushCompletionRef.current = null;
          // Resolve instead of reject: the worklet batches at most ~21ms of
          // audio, so an unanswered flush (e.g. the context is suspended after
          // a pause and the message sits queued) must not fail the entire
          // stop/save flow over an inconsequential tail of audio.
          console.warn(
            "Timed out while flushing the audio processor; continuing without the pending batch.",
          );
          resolve();
        }
      }, 2000);

      workletFlushCompletionRef.current = completion;

      audioWorkletNodeRef.current?.port.postMessage({ type: "flush" });
    });
  };

  const sendSingleChunk = async (chunk: StoredAudioChunk) => {
    const result = await sendTranscriptRequest(
      {
        action: "addChunk",
        transcriptId: chunk.sessionId,
        sequenceNumber: chunk.sequenceNumber,
        chunk: Array.from(new Uint8Array(chunk.data)),
      },
      {
        timeoutMs: Math.min(
          CHUNK_REQUEST_TIMEOUT_MS * (chunk.retryCount + 1),
          CHUNK_REQUEST_TIMEOUT_MAX_MS,
        ),
      },
    );

    if (result.success) {
      lastChunkSentAtRef.current = Date.now();
      uploadBlockedRef.current = false;
      lastUploadFailureWasFatalRef.current = false;
      uploadedBytesRef.current += chunk.byteLength;
      await touchLiveRecordingRecoveryManifest(chunk.sessionId, {
        uploadedBytes: uploadedBytesRef.current,
        lastUploadedSequenceNumber: chunk.sequenceNumber,
        lastErrorMessage: null,
      });
      return true;
    }

    lastUploadFailureWasFatalRef.current = result.blocked;

    if (result.blocked) {
      await blockRecording(
        result.errorMessage ||
          "This recording upload is blocked by the server. Audio is backed up on this device.",
      );
      return false;
    }

    hasNetworkErrorRef.current = true;
    setLastErrorMessage(
      "Upload is waiting for connection. Audio is backed up on this device.",
    );
    return false;
  };

  const processChunkQueue = async () => {
    if (isProcessingQueueRef.current) {
      return;
    }

    if (uploadBlockedRef.current) {
      return;
    }

    isProcessingQueueRef.current = true;

    try {
      if (!formatSentRef.current) {
        const formatReady = await sendFormatToServer();
        if (!formatReady) {
          return;
        }
      }

      while (pendingChunkCountRef.current > 0) {
        if (!isNetworkAvailable()) {
          hasNetworkErrorRef.current = true;
          if (
            pendingBytesRef.current >= FLUSH_THRESHOLD_BYTES ||
            stopRequestedRef.current
          ) {
            setLastErrorMessage(
              "Upload is waiting for connection. Audio is backed up on this device.",
            );
          }
          updateRecordingHealth();
          await scheduleRetry();
          return;
        }

        const sessionId = sessionIdRef.current;
        if (!sessionId) {
          return;
        }

        const chunk = await getNextStoredChunk(sessionId);
        if (!chunk) {
          pendingChunkCountRef.current = 0;
          pendingBytesRef.current = 0;
          updateRecordingHealth();
          break;
        }

        const success = await sendSingleChunk(chunk);

        if (!success) {
          if (!lastUploadFailureWasFatalRef.current) {
            if (chunk.retryCount >= MAX_CHUNK_UPLOAD_RETRIES) {
              await blockRecording("Upload retries were exhausted. Audio remains on this device; retry recovery when connected.");
              return;
            }
            await scheduleRetry(chunk);
          }
          return;
        }

        await deleteStoredChunk(chunk.id);
        pendingChunkCountRef.current = Math.max(
          0,
          pendingChunkCountRef.current - 1,
        );
        pendingBytesRef.current = Math.max(
          0,
          pendingBytesRef.current - chunk.byteLength,
        );
        hasNetworkErrorRef.current = false;

        if (
          pendingBytesRef.current < FLUSH_THRESHOLD_BYTES &&
          !stopRequestedRef.current
        ) {
          setLastErrorMessage(null);
        }
        updateRecordingHealth();
      }
    } finally {
      isProcessingQueueRef.current = false;
      finalizeStopIfReady();
    }
  };

  const stopInputTracksOnly = async () => {
    stopPolling();
    isRecordingRef.current = false;
    releaseInputRef.current?.();
    releaseInputRef.current = undefined;

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }
  };

  const suspendAudioContextOnly = async () => {
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      try {
        await audioContextRef.current.suspend();
      } catch (error) {
        console.error("Failed to suspend audio context:", error);
      }
    }
  };

  const stopCaptureOnly = async () => {
    await stopInputTracksOnly();
    await suspendAudioContextOnly();
  };

  const cleanupAudioResources = async () => {
    stopPolling();

    if (deviceChangeHandlerRef.current) {
      deviceChangeHandlerRef.current();
      deviceChangeHandlerRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.onstatechange = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.onended = null;
      });
    }

    if (audioWorkletNodeRef.current) {
      audioWorkletNodeRef.current.port.onmessage = null;
      audioWorkletNodeRef.current.disconnect();
      audioWorkletNodeRef.current = null;
      workletMessageHandlerRef.current = null;
    }

    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    releaseInputRef.current?.();
    releaseInputRef.current = undefined;

    if (audioContextRef.current) {
      try {
        await audioContextRef.current.close();
      } catch (error) {
        console.error("Error closing audio context:", error);
      }
      audioContextRef.current = null;
    }

    if (workletFlushCompletionRef.current) {
      workletFlushCompletionRef.current.reject(
        new Error("The audio pipeline was closed"),
      );
      workletFlushCompletionRef.current = null;
    }

    audioSegmentsRef.current = [];
    audioSegmentsTotalSamplesRef.current = 0;
    isRecordingRef.current = false;
  };

  const cleanup = async (options?: { clearLocalBackup?: boolean }) => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    clearRetryTimer();
    clearFormatRetryTimer();

    if (options?.clearLocalBackup && sessionIdRef.current) {
      try {
        await clearSessionChunks(sessionIdRef.current);
      } catch (error) {
        console.error("Failed to clear local recording backup:", error);
      }

      await clearLiveRecordingRecoveryManifest(sessionIdRef.current);
    }

    startTimeRef.current = null;
    durationRef.current = 0;
    setElapsedTime(0);
    audioFormatRef.current = {};
    formatSentRef.current = false;
    formatRetryCountRef.current = 0;
    hasNetworkErrorRef.current = false;
    pendingChunkCountRef.current = 0;
    pendingBytesRef.current = 0;
    uploadedBytesRef.current = 0;
    chunkSequenceNumberRef.current = 0;
    stopRequestedRef.current = false;
    localBackupReadyRef.current = true;
    hasDataLossRef.current = false;
    droppedChunksRef.current = 0;
    failedChunksRef.current = 0;
    lastErrorMessageRef.current = null;
    lastUploadFailureWasFatalRef.current = false;
    uploadBlockedRef.current = false;
    activePersistenceCountRef.current = 0;
    lastChunkSentAtRef.current = null;
    sessionIdRef.current = null;
    silenceStartedAtRef.current = null;
    isSilencedRef.current = false;
    micLostRef.current = false;
    micRecoveryInFlightRef.current = false;
    storageLowRef.current = false;
    // Reject any pending stop completion so awaiters don't hang forever.
    if (stopCompletionRef.current) {
      stopCompletionRef.current.reject(
        buildStopRecordingError({
          message:
            "The recording session was cleaned up before stopping completed.",
        }),
      );
      stopCompletionRef.current = null;
    }
    stopPromiseRef.current = null;

    await releaseWakeLock();
    await cleanupAudioResources();
    releaseSessionLockRef.current?.();
    releaseSessionLockRef.current = undefined;
  };

  const initAudioRecording = async () => {
    try {
      const transcriptId = getRecordingUiState().liveRecording?.itemId;
      if (!transcriptId) {
        throw new Error("Missing transcript id for live recording");
      }

      // Assign sessionId synchronously so any chunks produced by the worklet
      // before the rest of init completes are tagged correctly.
      sessionIdRef.current = transcriptId;
      await acquireSessionLock(transcriptId);

      const manifestPersisted = await setLiveRecordingRecoveryManifest({
        platform: "web",
        transcriptId,
        status: "recording",
        startedAt: Date.now(),
        uploadedBytes: 0,
        lastErrorMessage: null,
      });

      await purgeStaleChunks();
      const initialStats = await getSessionStats(transcriptId);

      pendingChunkCountRef.current = initialStats.pendingChunks;
      pendingBytesRef.current = initialStats.pendingBytes;
      uploadedBytesRef.current = 0;
      chunkSequenceNumberRef.current = initialStats.maxSequenceNumber;
      formatSentRef.current = false;
      formatRetryCountRef.current = 0;
      hasNetworkErrorRef.current = false;
      uploadBlockedRef.current = false;
      localBackupReadyRef.current = true;
      hasDataLossRef.current = false;
      droppedChunksRef.current = 0;
      failedChunksRef.current = 0;
      silenceStartedAtRef.current = null;
      isSilencedRef.current = false;
      micLostRef.current = false;
      storageLowRef.current = false;

      // Best-effort: ask the browser not to evict our IndexedDB backup under
      // storage pressure. A long offline recording can reach >1GB, and the
      // local backup may be the only copy.
      try {
        if (navigator.storage?.persist) {
          void navigator.storage.persist().catch(() => {});
        }
      } catch {
        // Older browsers without the Storage API.
      }

      // Surface the manifest-persistence warning AFTER the ref clearing block
      // so setLastErrorMessage(null) doesn't immediately overwrite it.
      if (!manifestPersisted) {
        setLastErrorMessage(
          "Crash recovery may be unavailable. The session is still being recorded and backed up locally.",
        );
      } else {
        setLastErrorMessage(null);
      }

      const input = await openRecordingAudioInput();
      streamRef.current = input.stream;
      releaseInputRef.current = input.release;

      audioContextRef.current = new AudioContext({ latencyHint: "balanced" });
      audioContextRef.current.onstatechange = handleAudioContextStateChange;
      sourceRef.current = audioContextRef.current.createMediaStreamSource(
        streamRef.current,
      );

      // Detect the mic dying mid-session (Bluetooth off, USB unplugged, OS
      // revocation) instead of silently recording nothing.
      streamRef.current.getTracks().forEach((track) => {
        track.onended = handleMicTrackEnded;
      });

      deviceChangeHandlerRef.current = attachRecordingDeviceChangeListener(
        navigator.mediaDevices,
        () => {
          if (micLostRef.current) {
            void recoverMicrophone();
          }
        },
      );

      const originalSampleRate = audioContextRef.current.sampleRate;
      audioFormatRef.current = {
        sampleRate: TARGET_SAMPLE_RATE,
        originalSampleRate,
        channels: 1,
        bitsPerSample: 16,
      };
      await touchLiveRecordingRecoveryManifest(transcriptId, {
        format: audioFormatRef.current as {
          sampleRate: number;
          originalSampleRate: number;
          channels: number;
          bitsPerSample: number;
        },
      });

      await audioContextRef.current.audioWorklet.addModule(
        "/worklets/audio-processor.js",
      );

      audioWorkletNodeRef.current = new AudioWorkletNode(
        audioContextRef.current,
        "audio-processor",
      );

      sourceRef.current.connect(audioWorkletNodeRef.current);
      audioWorkletNodeRef.current.connect(audioContextRef.current.destination);

      workletMessageHandlerRef.current = (event) => {
        if (!event.data) {
          return;
        }

        if (event.data.type === "flushComplete") {
          workletFlushCompletionRef.current?.resolve();
          workletFlushCompletionRef.current = null;
          return;
        }

        if (!event.data.audio_data) {
          return;
        }

        if (typeof event.data.audio_level === "number") {
          const now = Date.now();
          if (
            now - lastAudioLevelUpdateRef.current >=
            AUDIO_LEVEL_THROTTLE_MS
          ) {
            lastAudioLevelUpdateRef.current = now;
            setAudioLevel(event.data.audio_level);
          }

          trackSilence(event.data.audio_level);
        }

        const currentBuffer = new Int16Array(event.data.audio_data);
        const downsampledBuffer = downsampleAudio(
          currentBuffer,
          audioFormatRef.current.originalSampleRate || originalSampleRate,
          TARGET_SAMPLE_RATE,
        );

        reduceBitDepth(downsampledBuffer);

        audioSegmentsRef.current.push(downsampledBuffer);
        audioSegmentsTotalSamplesRef.current += downsampledBuffer.length;

        // Flush immediately while a stop is in flight: audio that arrives
        // after the stop flow's own flush passes (e.g. a worklet message
        // delivered late) would otherwise sit in the segment buffer with
        // nothing left to flush it, blocking the stop-completion gate until
        // the drain timeout.
        if (
          stopRequestedRef.current ||
          audioSegmentsTotalSamplesRef.current * 2 >= FLUSH_THRESHOLD_BYTES
        ) {
          void flushAudioBuffer();
        }
      };

      audioWorkletNodeRef.current.port.onmessage =
        workletMessageHandlerRef.current;
      startTimeRef.current = Date.now();
      isRecordingRef.current = true;
      notifyStatusChange("recording");
      await requestWakeLock();
      void processChunkQueue();

      return true;
    } catch (error) {
      console.error("Failed to initialize audio recording:", error);
      const failedTranscriptId = getRecordingUiState().liveRecording?.itemId;
      await clearLiveRecordingRecoveryManifest(failedTranscriptId);

      if (error instanceof DOMException && error.name === "NotAllowedError") {
        notifyStatusChange("permissionDenied");
      } else {
        notifyStatusChange("error", "Failed to initialize audio recording");
      }

      await cleanup();
      return false;
    }
  };

  const startRecording = async () => {
    if (
      getRecordingUiState().desktopCaptureActive ||
      ["starting", "recording", "paused", "uploading"].includes(status) ||
      startPromiseRef.current
    ) {
      return false;
    }

    const startPromise = (async () => {
      try {
        cancelPendingStartRef.current = false;
        setCurrentLiveRecordingStatus("starting");
        startTimeRef.current = null;
        durationRef.current = 0;
        setElapsedTime(0);

        const success = await initAudioRecording();
        if (!success) {
          return false;
        }

        if (
          cancelPendingStartRef.current ||
          !getRecordingUiState().liveRecording?.itemId
        ) {
          await cleanup({ clearLocalBackup: true });
          return false;
        }

        setupChunkProcessing(15);
        return true;
      } catch (error) {
        console.error("Failed to start recording:", error);
        notifyStatusChange("error", "Failed to start recording");
        await cleanup();
        return false;
      } finally {
        cancelPendingStartRef.current = false;
      }
    })();

    startPromiseRef.current = startPromise;

    try {
      return await startPromise;
    } finally {
      if (startPromiseRef.current === startPromise) {
        startPromiseRef.current = null;
      }
    }
  };

  const stopRecording = async (params: {
    onSuccess: () => Promise<void> | void;
    onError: (_error?: StopRecordingError) => Promise<void> | void;
  }) => {
    // A pause flush and a stop flush must not compete for the worklet's ack.
    if (pauseTransitionRef.current) await pauseTransitionRef.current;
    // Idempotent: if a stop is already in flight, await the same upload work
    // and forward the result to the new caller's callbacks. This makes a
    // double-tap of the stop button safe.
    if (stopPromiseRef.current) {
      try {
        await stopPromiseRef.current;
        await params.onSuccess();
        await cleanup({ clearLocalBackup: true });
      } catch (error) {
        await params.onError(
          error && typeof error === "object"
            ? (error as StopRecordingError)
            : buildStopRecordingError(),
        );
      }
      return;
    }

    const performStop = async () => {
      const waitForStopCompletion = new Promise<void>((resolve, reject) => {
        // Chain to any existing pending completion so duplicate stops resolve together.
        const existing = stopCompletionRef.current;
        stopCompletionRef.current = {
          resolve: () => {
            existing?.resolve();
            resolve();
          },
          reject: (error: StopRecordingError) => {
            existing?.reject(error);
            reject(error);
          },
        };
      });

      stopRequestedRef.current = true;
      isRecordingRef.current = false;
      notifyStatusChange(
        "uploading",
        "Finishing upload from the local backup. Keep the page open until the recording is saved.",
      );

      // Race the ENTIRE stop sequence (flush + drain + completion wait)
      // against a hard timeout. The drain performs network uploads, so a
      // single stalled request inside it would otherwise trap the user on the
      // spinner with the timeout never armed. On timeout the local backup
      // remains so recovery can pick it up on the next launch.
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(
          () =>
            reject(
              buildStopRecordingError({
                message:
                  "Upload is taking too long. The recording is backed up on this device and will retry the next time you open the app.",
                pendingChunks: pendingChunkCountRef.current,
                pendingBytes: pendingBytesRef.current,
                isLocalBackupReady: localBackupReadyRef.current,
              }),
            ),
          STOP_UPLOAD_DRAIN_TIMEOUT_MS,
        );
      });

      const drainAndWaitForCompletion = (async () => {
        await requestWorkletFlush();
        await flushAudioBuffer();
        await stopInputTracksOnly();
        // Stop the mic before the final drain so no new frames can slip in
        // between the first flush and the upload-completion gate check.
        await requestWorkletFlush();
        await flushAudioBuffer();
        await suspendAudioContextOnly();
        const transcriptId = sessionIdRef.current;
        if (transcriptId) {
          await touchLiveRecordingRecoveryManifest(transcriptId, { status: "uploading", finalSequenceNumber: chunkSequenceNumberRef.current });
          if (chunkSequenceNumberRef.current > 0) void reportRecordingStopped(transcriptId, chunkSequenceNumberRef.current).catch(() => undefined);
        }
        await processChunkQueue();
        finalizeStopIfReady();
        await waitForStopCompletion;
      })();

      // If the timeout wins the race, the drain keeps running in the
      // background until cleanup tears it down; swallow its eventual
      // rejection so it doesn't surface as an unhandled promise rejection.
      drainAndWaitForCompletion.catch(() => {});

      try {
        await Promise.race([drainAndWaitForCompletion, timeoutPromise]);
      } finally {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      }
    };

    const stopPromise = performStop();
    stopPromiseRef.current = stopPromise;

    try {
      await stopPromise;
      // Call onSuccess BEFORE clearing the local backup so a finalize failure
      // can be retried from the still-intact local recording.
      await params.onSuccess();
      await cleanup({ clearLocalBackup: true });
    } catch (error) {
      console.error("Failed to stop recording:", error);
      notifyStatusChange("error", "Failed to stop recording");
      await cleanup({ clearLocalBackup: false });
      await params.onError(
        error && typeof error === "object"
          ? (error as StopRecordingError)
          : buildStopRecordingError(),
      );
    } finally {
      stopPromiseRef.current = null;
    }
  };

  const pauseRecording = async () => {
    if (status !== "recording") {
      return;
    }

    try {
      isRecordingRef.current = false;
      resetSilenceTracking();
      await requestWorkletFlush();
      await flushAudioBuffer();

      if (audioContextRef.current?.state === "running") {
        await audioContextRef.current.suspend();
      }

      stopPolling();
      if (startTimeRef.current) {
        durationRef.current += Date.now() - startTimeRef.current;
        startTimeRef.current = null;
      }
      setElapsedTime(durationRef.current);
      notifyStatusChange("paused");
      await releaseWakeLock();

      await processChunkQueue();
    } catch (error) {
      console.error("Failed to pause recording:", error);
      notifyStatusChange("error", "Failed to pause recording");
    }
  };

  const resumeRecording = async () => {
    if (status !== "paused") {
      return;
    }

    try {
      if (audioContextRef.current?.state === "suspended") {
        await audioContextRef.current.resume();
      }

      isRecordingRef.current = true;
      startTimeRef.current = Date.now();
      silenceStartedAtRef.current = null;

      // The mic may have died while paused (its onended fires, but recovery
      // defers while paused) — or ended without the event firing at all.
      const hasLiveTrack = streamRef.current
        ?.getTracks()
        .some((track) => track.readyState === "live");
      if (!hasLiveTrack) {
        micLostRef.current = true;
      }
      if (micLostRef.current) {
        void recoverMicrophone();
      }

      notifyStatusChange("recording");
      setupChunkProcessing(15);
      await requestWakeLock();
      void processChunkQueue();
    } catch (error) {
      console.error("Failed to resume recording:", error);
      notifyStatusChange("error", "Failed to resume recording");
    }
  };

  const cancelRecording = async (
    callback: (result: { success: boolean; error?: unknown }) => void,
  ) => {
    if (status === "starting" || startPromiseRef.current) {
      cancelPendingStartRef.current = true;

      try {
        await cleanup({ clearLocalBackup: true });
        callback({ success: true });
      } catch (error) {
        console.error("Failed to cancel pending recording start:", error);
        callback({ success: false, error });
      }
      return;
    }

    if (!["recording", "paused", "uploading"].includes(status)) {
      callback({ success: true });
      return;
    }

    try {
      notifyStatusChange("cancelled");
      await cleanup({ clearLocalBackup: true });
      callback({ success: true });
    } catch (error) {
      console.error("Failed to cancel recording:", error);
      notifyStatusChange("error", "Failed to cancel recording");
      await cleanup({ clearLocalBackup: false });
      callback({ success: false, error });
    }
  };

  const getRecoveryStatus =
    async (excludeTranscriptIds: readonly string[] = []): Promise<RecordingRecoveryStatus | null> => {
      let manifest = await getLiveRecordingRecoveryForPlatform("web", excludeTranscriptIds);
      if (!manifest) return null;
      const held = navigator.locks ? (await navigator.locks.query()).held ?? [] : [];
      const activeIds = held.map(lock => lock.name?.replace(/^recording:/, "") || "");
      if (activeIds.includes(manifest.transcriptId)) {
        manifest = await getLiveRecordingRecoveryForPlatform("web", [...excludeTranscriptIds, ...activeIds]);
        if (!manifest) return null;
      }

      try {
        const stats = await getSessionStats(manifest.transcriptId);
        return {
          transcriptId: manifest.transcriptId,
          startedAt: manifest.startedAt,
          pendingChunks: stats.pendingChunks,
          pendingBytes: stats.pendingBytes,
          finalSequenceNumber: manifest.finalSequenceNumber ?? Math.max(stats.maxSequenceNumber, manifest.lastUploadedSequenceNumber ?? 0),
          uploadedBytes: manifest.uploadedBytes ?? 0,
          recoveryMode: manifest.status === "blocked" ? "blocked" : "pending",
          lastErrorMessage: manifest.lastErrorMessage ?? null,
        };
      } catch (error) {
        console.error(
          "Failed to inspect web recording recovery status:",
          error,
        );
        return {
          transcriptId: manifest.transcriptId,
          startedAt: manifest.startedAt,
          uploadedBytes: manifest.uploadedBytes,
          recoveryMode: manifest.status === "blocked" ? "blocked" : "pending",
          lastErrorMessage: manifest.lastErrorMessage ?? null,
        };
      }
    };

  const resumeRecoveryUpload = async (params: {
    transcriptId: string;
    retryBlocked?: boolean;
  }) => {
    const manifest = await getLiveRecordingRecoveryForTranscript(
      params.transcriptId,
    );
    if (!manifest || manifest.platform !== "web") {
      return;
    }

    if (manifest.status === "blocked" && !params.retryBlocked) {
      throw new Error(
        manifest.lastErrorMessage ||
          "This interrupted recording needs attention before it can continue uploading.",
      );
    }

    const waitForStopCompletion = new Promise<void>((resolve, reject) => {
      stopCompletionRef.current = { resolve, reject };
    });

    try {
      await acquireSessionLock(params.transcriptId);
      const stats = await getSessionStats(params.transcriptId);

      const finalSequenceNumber = manifest.finalSequenceNumber ?? Math.max(stats.maxSequenceNumber, manifest.lastUploadedSequenceNumber ?? 0);
      await touchLiveRecordingRecoveryManifest(params.transcriptId, { status: "uploading", finalSequenceNumber });
      if (finalSequenceNumber > 0) void reportRecordingStopped(params.transcriptId, finalSequenceNumber).catch(() => undefined);
      if (stats.pendingChunks <= 0) {
        // All chunks already reached the server; only the finalize step is
        // outstanding. Do NOT clear the manifest here — the caller runs
        // onRecovered (server finalize) next and then discardRecovery, which
        // deletes the manifest. Clearing it now would orphan the recording if
        // that finalize fails: fully uploaded server-side, but with nothing
        // left locally to trigger a retry on the next launch.
        stopCompletionRef.current = null;
        releaseSessionLockRef.current?.();
        releaseSessionLockRef.current = undefined;
        return;
      }

      sessionIdRef.current = params.transcriptId;
      pendingChunkCountRef.current = stats.pendingChunks;
      pendingBytesRef.current = stats.pendingBytes;
      uploadedBytesRef.current = manifest.uploadedBytes ?? 0;
      chunkSequenceNumberRef.current = stats.maxSequenceNumber;
      audioFormatRef.current = getWebRecoveryFormat(manifest);
      formatSentRef.current = false;
      formatRetryCountRef.current = 0;
      stopRequestedRef.current = true;
      uploadBlockedRef.current = false;
      localBackupReadyRef.current = true;
      hasDataLossRef.current = false;
      hasNetworkErrorRef.current = false;
      setLastErrorMessage(null);

      notifyStatusChange(
        "uploading",
        "Finishing upload from the local backup. Keep the page open until the recording is saved.",
      );

      await setLiveRecordingRecoveryManifest({
        platform: "web",
        transcriptId: params.transcriptId,
        status: "uploading",
        startedAt: manifest.startedAt,
        finalSequenceNumber,
        lastUploadedSequenceNumber: manifest.lastUploadedSequenceNumber,
        format: getWebRecoveryFormat(manifest),
        uploadedBytes: uploadedBytesRef.current,
        lastErrorMessage: null,
      });

      await processChunkQueue();
      finalizeStopIfReady();

      // Race the upload-drain wait against the same timeout used by stopRecording.
      // Without this, offline / auth-loss during recovery traps the user on the
      // recovery spinner indefinitely.
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(
          () =>
            reject(
              new Error(
                "Recovery upload is taking too long. The recording remains backed up and will retry on the next launch.",
              ),
            ),
          STOP_UPLOAD_DRAIN_TIMEOUT_MS,
        );
      });

      try {
        await Promise.race([waitForStopCompletion, timeoutPromise]);
      } finally {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      }

      // Do NOT clearLocalBackup here. The caller (recoverInterruptedLiveRecording)
      // still needs to run onRecovered → server finalize. Only after that succeeds
      // does it call discardRecovery(), which is what actually deletes the chunks
      // and manifest. If we wiped them now a finalize failure would lose the retry.
      await cleanup({ clearLocalBackup: false });
    } catch (error) {
      console.error("Failed to resume recovery upload:", error);
      notifyStatusChange("error", "Failed to recover and save this recording.");
      stopCompletionRef.current = null;
      // Tear down the queue state (stopRequested flag, retry timers) so a
      // failed recovery doesn't leave a background retry loop running until
      // the tab closes. The local backup is kept so the next launch retries.
      await cleanup({ clearLocalBackup: false });
      throw error;
    }
  };

  const discardRecovery = async (params: { transcriptId: string }) => {
    try {
      await clearSessionChunks(params.transcriptId);
    } catch (error) {
      console.error("Failed to clear recovery chunks:", error);
    }

    await clearLiveRecordingRecoveryManifest(params.transcriptId);
  };

  const getQueueStatus = () => ({
    pendingChunks: pendingChunkCountRef.current,
    pendingBytes: pendingBytesRef.current,
    hasNetworkError: hasNetworkErrorRef.current,
    isNetworkAvailable: isNetworkAvailable(),
    isServerReady: formatSentRef.current,
    isLocalBackupReady: localBackupReadyRef.current,
    hasDataLoss: hasDataLossRef.current,
    droppedChunks: droppedChunksRef.current,
    failedChunks: failedChunksRef.current,
  });

  useEffect(() => {
    if (status === "recording") {
      timerIntervalRef.current = setInterval(() => {
        if (startTimeRef.current) {
          setElapsedTime(
            Date.now() - startTimeRef.current + durationRef.current,
          );
        }
      }, 1000);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }

      if (
        status === "inactive" ||
        status === "cancelled" ||
        status === "error" ||
        status === "permissionDenied"
      ) {
        setElapsedTime(0);
      }
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [status]);

  useEffect(() => {
    const handleOnline = () => {
      hasNetworkErrorRef.current = false;
      if (
        pendingChunkCountRef.current > 0 &&
        ["recording", "paused", "uploading"].includes(statusRef.current)
      ) {
        void processChunkQueue();
      } else {
        updateRecordingHealth();
      }
    };

    const handleOffline = () => {
      hasNetworkErrorRef.current = true;
      if (
        pendingBytesRef.current >= FLUSH_THRESHOLD_BYTES ||
        stopRequestedRef.current
      ) {
        setLastErrorMessage(
          "Upload is waiting for connection. Audio is backed up on this device.",
        );
      }
      updateRecordingHealth();
    };

    return attachRecordingNetworkListeners(window, {
      online: handleOnline,
      offline: handleOffline,
    });
    // The handlers read everything they need via refs (statusRef,
    // pendingChunkCountRef, etc.), so we deliberately omit the function deps
    // to avoid re-binding the listeners on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible" && status === "recording") {
        await requestWakeLock();

        // Coming back to the tab is the natural moment to repair anything the
        // OS broke while we were backgrounded: a suspended context (mobile
        // Safari interruptions) or a lost microphone.
        const audioContext = audioContextRef.current;
        if (
          audioContext &&
          (audioContext.state as string) !== "running" &&
          (audioContext.state as string) !== "closed" &&
          isRecordingRef.current &&
          !stopRequestedRef.current
        ) {
          try {
            await audioContext.resume();
          } catch (error) {
            console.error(
              "Failed to resume audio context on visibility:",
              error,
            );
          }
        }

        if (micLostRef.current) {
          void recoverMicrophone();
        }
      }
    };

    return attachRecordingVisibilityListener(document, handleVisibilityChange);
    // recoverMicrophone reads all its state via refs and is recreated each
    // render; including it would re-bind the listener every render for no
    // behavioral difference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (
        statusRef.current === "recording" ||
        statusRef.current === "uploading" ||
        pendingChunkCountRef.current > 0 ||
        activePersistenceCountRef.current > 0
      ) {
        event.preventDefault();
        event.returnValue = "";
      }
    };

    return attachRecordingBeforeUnloadListener(window, handleBeforeUnload);
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (!["recording", "paused", "uploading"].includes(statusRef.current)) {
        return;
      }

      if (
        pendingChunkCountRef.current > 0 &&
        isNetworkAvailable() &&
        !isProcessingQueueRef.current
      ) {
        void processChunkQueue();
      }
    }, 5000);

    return () => clearInterval(intervalId);
    // statusRef and pendingChunkCountRef are read via .current so we don't
    // need processChunkQueue in the deps; re-binding the interval would
    // restart the 5s timer on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!["recording", "paused", "uploading", "error"].includes(status)) {
      setRecordingHealth({
        status: "healthy",
        pendingChunks: 0,
        hasNetworkError: false,
        isNetworkAvailable: true,
        lastChunkSentAt: null,
        isServerReady: true,
        isLocalBackupReady: true,
        hasDataLoss: false,
        droppedChunks: 0,
        failedChunks: 0,
        lastErrorMessage: null,
      });
      setAudioLevel(0);
      return;
    }

    updateRecordingHealth();
    const intervalId = setInterval(updateRecordingHealth, 1000);
    return () => clearInterval(intervalId);
    // updateRecordingHealth reads from refs so it's safe to omit from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    return () => {
      void cleanup({ clearLocalBackup: false });
    };
    // Cleanup runs once on unmount with the latest closure-captured cleanup
    // function. The function reads everything it needs via refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    status,
    elapsedTime,
    audioLevel,
    recordingHealth,
    pauseOrPlay: async () => {
      if (stopPromiseRef.current) return;
      if (pauseTransitionRef.current) return pauseTransitionRef.current;
      const pending =
        getRecordingUiState().liveRecording?.status === "paused"
          ? resumeRecording()
          : pauseRecording();
      pauseTransitionRef.current = pending;
      try {
        await pending;
      } finally {
        if (pauseTransitionRef.current === pending)
          pauseTransitionRef.current = undefined;
      }
    },
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cancelRecording,
    getRecoveryStatus,
    resumeRecoveryUpload,
    discardRecovery,
    getQueueStatus,
  };
};
